import { useEffect, useRef, useState } from "react";

type Permiso = "default" | "granted" | "denied" | "unsupported";

/**
 * Detecta el browser support, pide permiso bajo demanda y emite notificaciones nativas
 * cuando un valor `count` aumenta entre renders. Útil para bandejas con polling.
 *
 * Usage:
 *   const { permiso, solicitar, notificarSiCrece } =
 *     useBrowserNotifications();
 *   useEffect(() => {
 *     notificarSiCrece(data.length, {
 *       titulo: "Nueva solicitud por autorizar",
 *       cuerpo: `Tienes ${data.length} pendientes.`,
 *     });
 *   }, [data.length]);
 */
export function useBrowserNotifications() {
  const [permiso, setPermiso] = useState<Permiso>("default");
  const ultimoValor = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setPermiso("unsupported");
      return;
    }
    setPermiso(Notification.permission as Permiso);
  }, []);

  const solicitar = async (): Promise<Permiso> => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setPermiso("unsupported");
      return "unsupported";
    }
    const resultado = await Notification.requestPermission();
    const next = resultado as Permiso;
    setPermiso(next);
    return next;
  };

  const notificarSiCrece = (
    valorActual: number,
    opciones: { titulo: string; cuerpo?: string; icono?: string; tag?: string }
  ) => {
    const prev = ultimoValor.current;
    ultimoValor.current = valorActual;

    // Primera lectura · no notificar (es el snapshot inicial al montar).
    if (prev === null) return;
    if (valorActual <= prev) return;
    if (permiso !== "granted") return;
    if (document.visibilityState === "visible") return; // No interrumpir si la pestaña ya está activa.

    try {
      new Notification(opciones.titulo, {
        body: opciones.cuerpo,
        icon: opciones.icono,
        tag: opciones.tag ?? "multiva-bandeja",
      });
    } catch {
      // Best-effort.
    }
  };

  return { permiso, solicitar, notificarSiCrece };
}
